import React, { useState, useEffect } from 'react';
import './VentanaFlotanteCambios.css';
import catalogService from '../utils/catalogService';
import formsService from '../utils/formsService';

/**
 * Componente de ventana flotante que muestra el resumen de cambios
 * y permite enviar los datos al servidor
 */
const VentanaFlotanteCambios = ({ 
  totalCambios, 
  obtenerResumenCambios, 
  obtenerDatosParaEnvio,
  infoGeneral,
  onEnviarCambios,
  onLimpiarCambios,
  onDescartarCambios,
  visible = true 
}) => {
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetalles, setErrorDetalles] = useState(null);
  const [exito, setExito] = useState(false);
  const [displayLabels, setDisplayLabels] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});

  /* Guard moved below hooks to keep hooks unconditional */

  const resumenCambios = obtenerResumenCambios();

  // Resetear mensajes (error/éxito) y acordeón cuando se oculta o no hay cambios
  useEffect(() => {
    if (!visible || totalCambios === 0) {
      setError(null);
      setErrorDetalles(null);
      setExito(false);
      setMostrarDetalle(false);
    }
  }, [visible, totalCambios]);

  // Al cambiar el conjunto de cambios (p. ej., moverse de sección o editar otras preguntas), limpiar mensajes
  useEffect(() => {
    if (visible && totalCambios > 0) {
      setError(null);
      setErrorDetalles(null);
      setExito(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(resumenCambios)]);

  useEffect(() => {
    const calcular = async () => {
      const nuevoDisplay = {};
      const nuevasPreviews = {};

      for (const cambio of resumenCambios) {
        const tipoBase = ((cambio.tipo || cambio.pregunta?.type || '')).split(' ')[0];

        // Etiquetas amigables para selects
        if (tipoBase === 'select_one' || tipoBase === 'select_multiple') {
          const listName = extraerCatalogo(cambio.tipo || cambio.pregunta?.type);
          const valoresOriginales = normalizarValoresSeleccionPorTipo(cambio.valorOriginal, tipoBase);
          const valoresNuevos = normalizarValoresSeleccionPorTipo(cambio.valorNuevo, tipoBase);

          if (listName) {
            const labelsOriginales = await mapearValoresAetiquetasPorCatalogo(listName, valoresOriginales);
            const labelsNuevos = await mapearValoresAetiquetasPorCatalogo(listName, valoresNuevos);
            nuevoDisplay[cambio.campo] = {
              original: labelsOriginales.join(', '),
              nuevo: labelsNuevos.join(', '),
            };
          } else {
            // Intentar con choices embebidos en la pregunta
            const preguntaConfig = buscarPreguntaPorNombre(cambio.campo);
            const choices = preguntaConfig?.choices || preguntaConfig?.options;
            const labelsOriginales = mapearValoresAetiquetasPorChoices(choices, valoresOriginales);
            const labelsNuevos = mapearValoresAetiquetasPorChoices(choices, valoresNuevos);
            nuevoDisplay[cambio.campo] = {
              original: labelsOriginales.join(', '),
              nuevo: labelsNuevos.join(', '),
            };
          }
        }

        // Previsualización de imágenes para type=image
        if (tipoBase === 'image') {
          const previews = { original: null, nuevo: null };
          // Valor original
          try {
            const vO = cambio.valorOriginal;
            if (vO && typeof vO === 'object' && vO.isFile && vO.file) {
              previews.original = URL.createObjectURL(vO.file);
            } else if (typeof vO === 'string' && vO.trim()) {
              previews.original = await formsService.getImageThumbnail(vO);
            }
          } catch (_) {
            previews.original = null;
          }
          // Valor nuevo
          try {
            const vN = cambio.valorNuevo;
            if (vN && typeof vN === 'object' && vN.isFile && vN.file) {
              previews.nuevo = URL.createObjectURL(vN.file);
            } else if (typeof vN === 'string' && vN.trim()) {
              previews.nuevo = await formsService.getImageThumbnail(vN);
            }
          } catch (_) {
            previews.nuevo = null;
          }

          nuevasPreviews[cambio.campo] = previews;
        }
      }

      setDisplayLabels(nuevoDisplay);
      setImagePreviews(nuevasPreviews);
    };

    if (!visible || totalCambios === 0) {
      // Liberar URLs blob previas si existen
      try {
        Object.values(imagePreviews || {}).forEach(({ original, nuevo }) => {
          if (original && String(original).startsWith('blob:')) URL.revokeObjectURL(original);
          if (nuevo && String(nuevo).startsWith('blob:')) URL.revokeObjectURL(nuevo);
        });
      } catch {}
      setDisplayLabels({});
      setImagePreviews({});
      return;
    }

    calcular();

    return () => {
      // Cleanup: liberar URLs blob anteriores
      try {
        Object.values(imagePreviews || {}).forEach(({ original, nuevo }) => {
          if (original && String(original).startsWith('blob:')) URL.revokeObjectURL(original);
          if (nuevo && String(nuevo).startsWith('blob:')) URL.revokeObjectURL(nuevo);
        });
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(resumenCambios), visible, totalCambios]);

  const extraerDetallesErrorServidor = (err) => {
    const status = err?.status || err?.response?.status;
    const serverMessage = err?.serverMessage || err?.response?.data?.message;
    const texto = err?.message || (status ? `Error (${status})` : 'Error al enviar los cambios');
    return { texto, detalles: serverMessage };
  };

  const manejarEnvio = async () => {
    try {
      setEnviando(true);
      setError(null);
      setErrorDetalles(null);
      setExito(false);

      const datosParaEnvio = obtenerDatosParaEnvio(infoGeneral);
      // console.log('📤 [ENVÍO] Datos a enviar:', datosParaEnvio);

      await onEnviarCambios(datosParaEnvio);
      
      setExito(true);
      setTimeout(() => {
        setExito(false);
        // Tras enviar exitosamente, únicamente limpiar seguimiento de cambios
        if (typeof onLimpiarCambios === 'function') {
          onLimpiarCambios();
        }
      }, 2000);

    } catch (err) {
      const { texto, detalles } = extraerDetallesErrorServidor(err);
      setError(texto);
      setErrorDetalles(detalles);
    } finally {
      setEnviando(false);
    }
  };

  const manejarDescartar = () => {
    // Limpiar estados locales de mensajes al descartar
    setError(null);
    setErrorDetalles(null);
    setExito(false);
    setMostrarDetalle(false);
    if (typeof onDescartarCambios === 'function') return onDescartarCambios();
    if (typeof onLimpiarCambios === 'function') return onLimpiarCambios();
  };

  const formatearValor = (valor, tipo) => {
    if (valor === null || valor === undefined) return 'Sin valor';
    if (typeof valor === 'object' && valor.isFile) return valor.name;
    if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
    if (Array.isArray(valor)) return valor.join(', ');
    return String(valor);
  };

  // Helpers para resolver etiquetas de selects
  const extraerCatalogo = (tipo) => {
    if (!tipo || typeof tipo !== 'string') return null;
    const partes = tipo.split(' ');
    return partes.length > 1 ? partes[1] : null;
  };

  const normalizarValoresSeleccionPorTipo = (raw, tipoBase) => {
    if (raw === null || raw === undefined) return [];
    if (Array.isArray(raw)) return raw.map(v => String(v)).filter(s => s.trim() !== '');
    const s = String(raw);
    // Para select_one no dividir el valor; tratarlo como un único elemento
    if (tipoBase === 'select_one') {
      return [s];
    }
    // Para select_multiple permitir separación por coma o por espacio
    if (tipoBase === 'select_multiple') {
      if (s.includes(',')) return s.split(',').map(x => x.trim()).filter(Boolean);
      if (s.includes(' ')) return s.split(' ').map(x => x.trim()).filter(Boolean);
      return [s];
    }
    // Otros tipos: retornar como un solo valor
    return [s];
  };

  const buscarPreguntaPorNombre = (name) => {
    const config = formsService.getConfigForm();
    if (!config || !Array.isArray(config)) return null;

    const stack = [...config];
    while (stack.length > 0) {
      const node = stack.shift();
      if (!node) continue;
      const baseType = (node.type || '').split(' ')[0];
      if (baseType === 'begin_group' || baseType === 'begin_repeat' || baseType === 'group') {
        if (Array.isArray(node.children)) {
          stack.push(...node.children);
        }
        continue;
      }
      if (node.name === name) return node;
    }
    return null;
  };

  const mapearValoresAetiquetasPorCatalogo = async (listName, values) => {
    if (!listName) return values.map(v => String(v));
    try {
      const options = await catalogService.getOptions(listName);
      return values.map(v => {
        const opt = options.find(o => String(o.originalId) === String(v) || String(o.value) === String(v) || String(o.id) === String(v));
        return opt ? opt.label : String(v);
      });
    } catch (e) {
      return values.map(v => String(v));
    }
  };

  const mapearValoresAetiquetasPorChoices = (choices, values) => {
    if (!choices || !Array.isArray(choices)) return values.map(v => String(v));
    return values.map(v => {
      const ch = choices.find(c => String(c.name || c) === String(v));
      return ch ? (ch.label || ch.name || String(v)) : String(v);
    });
  };

  // Guard to prevent render while keeping hooks unconditional
  if (!visible || totalCambios === 0) {
    return null;
  }

  return (
    <div className="ventana-flotante-cambios">
      <div className="ventana-flotante-header">
        <div className="cambios-contador">
          <span className="cambios-icono">📝</span>
          <span className="cambios-texto">
            {totalCambios} cambio{totalCambios !== 1 ? 's' : ''} realizados
          </span>
        </div>
        
        <div className="ventana-flotante-acciones">
          <button
            className="btn-toggle-detalle"
            onClick={() => setMostrarDetalle(!mostrarDetalle)}
            title={mostrarDetalle ? 'Ocultar detalles' : 'Ver detalles'}
          >
            {mostrarDetalle ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {mostrarDetalle && (
        <div className="ventana-flotante-detalle">
          <div className="cambios-lista">
            {resumenCambios.map((cambio, index) => (
              <div key={`${cambio.campo}-${index}`} className="cambio-item">
                <div className="cambio-campo">
                  <strong>{cambio.etiqueta || cambio.campo}</strong>
                </div>
                <div className="cambio-valores">
                  <div className="valor-anterior">
                    <span className="valor-label">Anterior:</span>
                    <span className="valor-contenido">
                      {(() => {
                        const tipoBase = ((cambio.tipo || cambio.pregunta?.type || '')).split(' ')[0];
                        if ((tipoBase === 'select_one' || tipoBase === 'select_multiple') && displayLabels[cambio.campo]) {
                          return displayLabels[cambio.campo].original || formatearValor(cambio.valorOriginal, cambio.tipo);
                        }
                        if (tipoBase === 'image' && imagePreviews[cambio.campo]?.original) {
                          return (
                            <img
                              src={imagePreviews[cambio.campo].original}
                              alt={`Imagen anterior de ${cambio.etiqueta || cambio.campo}`}
                              style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                              onError={() => {}}
                            />
                          );
                        }
                        if (tipoBase === 'image') {
                          const vO = cambio.valorOriginal;
                          const tieneValor = !!(
                            (vO && typeof vO === 'object' && vO.file) ||
                            (typeof vO === 'string' && vO.trim())
                          );
                          return tieneValor ? 'Imagen no encontrada' : 'Sin valor';
                        }
                        return formatearValor(cambio.valorOriginal, cambio.tipo);
                      })()}
                    </span>
                  </div>
                  <div className="valor-nuevo">
                    <span className="valor-label">Nuevo:</span>
                    <span className="valor-contenido">
                      {(() => {
                        const tipoBase = ((cambio.tipo || cambio.pregunta?.type || '')).split(' ')[0];
                        if ((tipoBase === 'select_one' || tipoBase === 'select_multiple') && displayLabels[cambio.campo]) {
                          return displayLabels[cambio.campo].nuevo || formatearValor(cambio.valorNuevo, cambio.tipo);
                        }
                        if (tipoBase === 'image' && imagePreviews[cambio.campo]?.nuevo) {
                          return (
                            <img
                              src={imagePreviews[cambio.campo].nuevo}
                              alt={`Imagen nueva de ${cambio.etiqueta || cambio.campo}`}
                              style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                              onError={() => {}}
                            />
                          );
                        }
                        if (tipoBase === 'image') {
                          const vN = cambio.valorNuevo;
                          const tieneValor = !!(
                            (vN && typeof vN === 'object' && vN.file) ||
                            (typeof vN === 'string' && vN.trim())
                          );
                          return tieneValor ? 'Imagen no encontrada' : 'Sin valor';
                        }
                        return formatearValor(cambio.valorNuevo, cambio.tipo);
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ventana-flotante-footer">
        {error && (
          <div className="mensaje-error">
            <span className="error-icono">⚠️</span>
            <span className="error-texto">{error}</span>
          </div>
        )}

        {/* Detalles del error del servidor */}
        {errorDetalles && (
          <div className="mensaje-error-detalles">


            {Array.isArray(errorDetalles?.errores) && errorDetalles.errores.length > 0 && (
              <div className="error-lista">
                <div style={{ fontWeight: 'bold', marginTop: 6 }}>Detalles de errores:</div>
                {errorDetalles.errores.map((e, idx) => (
                  <div key={`${e.campo}-${idx}`} className="error-item">
                    <div>
                      <strong>{e.campo}</strong>
                    </div>
                    <div style={{ color: '#b00020' }}>{e.error}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {exito && (
          <div className="mensaje-exito">
            <span className="exito-icono">✅</span>
            <span className="exito-texto">Cambios enviados correctamente</span>
          </div>
        )}

        <div className="botones-accion">
          <button
            className="btn-limpiar"
            onClick={manejarDescartar}
            disabled={enviando}
            title="Descartar cambios"
          >
            Descartar
          </button>
          
          <button
            className="btn-enviar"
            onClick={manejarEnvio}
            disabled={enviando || totalCambios === 0}
            title="Enviar cambios al servidor"
          >
            {enviando ? (
              <>
                <span className="spinner">⏳</span>
                Enviando...
              </>
            ) : (
              <>
                Enviar Cambios
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VentanaFlotanteCambios;